import React, { useState, useEffect, useMemo } from 'react';
import { View, Text, ScrollView, Pressable, StyleSheet, Platform, ActivityIndicator, Linking } from 'react-native';
import { useStyles } from '../../hooks/useStyles';
import { useResponsive } from '../../shared/utils/responsive';
import { addColorOpacity } from '../../shared/utils/styles';
import { authApi, SmartContact, SmartContactsResponse } from '../../shared/utils/api';
import { ContactType } from '../../shared/types/contact';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { ContactFormModal } from '../contacts/ContactFormModal';
import { Ionicons } from '@expo/vector-icons';
import { useToast } from '../toast';

// Icon mapping for contact types
const TYPE_ICONS: Record<string, string> = {
  lender: 'card-outline',
  banker: 'briefcase-outline',
  contractor: 'hammer-outline',
  real_estate_agent: 'home-outline',
  inspector: 'search-outline',
  attorney: 'scale-outline',
  accountant: 'calculator-outline',
  insurance_agent: 'shield-outline',
  property_manager: 'business-outline',
  vendor: 'cube-outline',
  other: 'person-outline',
  surveyor: 'compass-outline',
  title_company: 'document-text-outline',
  septic_service: 'water-outline',
  well_service: 'rainy-outline',
  soil_lab: 'layers-outline',
  environmental: 'leaf-outline',
  forester: 'flower-outline',
  appraiser: 'trending-up-outline',
  engineer: 'construct-outline',
  utility_company: 'flash-outline',
  government: 'business-outline',
  neighbor: 'people-outline',
  farmer_rancher: 'nutrition-outline',
  veterinarian: 'medkit-outline',
  pest_control: 'bug-outline',
};

export interface PropertyContactsProps {
  portfolioId: number;
  propertyId: number;
}

export function PropertyContacts({ portfolioId, propertyId }: PropertyContactsProps) {
  const styles = useStyles();
  const { tokens, utils } = styles;
  const { isMobile, isSmallMobile, isDesktop } = useResponsive();
  const toast = useToast();
  const contactStyles = makeContactStyles(tokens, utils, isMobile, isSmallMobile, isDesktop);

  const [loading, setLoading] = useState(true);
  const [contactsData, setContactsData] = useState<SmartContactsResponse | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [contactTypes, setContactTypes] = useState<ContactType[]>([]);
  const [formModalVisible, setFormModalVisible] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, [portfolioId, propertyId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [contactsResponse, typesResponse] = await Promise.all([
        authApi.getSmartContacts(portfolioId, propertyId),
        authApi.getContactTypes(),
      ]);
      setContactsData(contactsResponse);
      setContactTypes(typesResponse.types);
    } catch (error) {
      console.error('Failed to load contacts:', error);
      toast.showError('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  // Filter contacts
  const filteredContacts = useMemo(() => {
    if (!contactsData) return [];
    let contacts = contactsData.contacts.all;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      contacts = contacts.filter(c =>
        c.first_name?.toLowerCase().includes(query) ||
        c.last_name?.toLowerCase().includes(query) ||
        c.company_name?.toLowerCase().includes(query) ||
        c.email?.toLowerCase().includes(query)
      );
    }

    if (selectedType) {
      contacts = contacts.filter(c => c.contact_type.name === selectedType);
    }

    return contacts;
  }, [contactsData, searchQuery, selectedType]);

  // Group by type
  const contactsByType = useMemo(() => {
    const grouped: Record<string, SmartContact[]> = {};
    filteredContacts.forEach(c => {
      const type = c.contact_type.name;
      if (!grouped[type]) grouped[type] = [];
      grouped[type].push(c);
    });
    return grouped;
  }, [filteredContacts]);

  // Get unique types from contacts
  const availableTypes = useMemo(() => {
    if (!contactsData) return [];
    const types = new Set<string>();
    contactsData.contacts.all.forEach(c => types.add(c.contact_type.name));
    return Array.from(types).sort();
  }, [contactsData]);

  const handleCall = (phone: string) => {
    Linking.openURL(`tel:${phone}`);
  };

  const handleEmail = (email: string) => {
    Linking.openURL(`mailto:${email}`);
  };

  const handleCopy = async (text: string, label: string) => {
    try {
      if (Platform.OS === 'web' && navigator.clipboard) {
        await navigator.clipboard.writeText(text);
        toast.showSuccess(`${label} copied`);
      }
    } catch (e) {
      toast.showError('Failed to copy');
    }
  };

  const handleAddContact = async (data: any) => {
    try {
      setSaving(true);
      // Add as property-level contact
      await authApi.createPropertyContact(portfolioId, propertyId, data);
      toast.showSuccess('Contact added');
      setFormModalVisible(false);
      loadData();
    } catch (error) {
      toast.showError('Failed to add contact');
    } finally {
      setSaving(false);
    }
  };

  const formatTypeName = (name: string) => {
    return name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const renderContactCard = (contact: SmartContact) => {
    const name = contact.company_name ||
      `${contact.first_name || ''} ${contact.last_name || ''}`.trim() ||
      'Unnamed';
    const icon = TYPE_ICONS[contact.contact_type.name] || 'person-outline';
    const addedByLabel = contact.added_by_info.isCurrentUser
      ? 'You'
      : contact.added_by_info.display_name || contact.added_by_info.email.split('@')[0];
    const sourceLabel = contact.source === 'property' ? 'Property' :
      contact.source === 'portfolio' ? 'Portfolio' : 'Personal';

    return (
      <Card key={contact.id} style={contactStyles.contactCard}>
        <View style={contactStyles.cardHeader}>
          <View style={contactStyles.iconContainer}>
            <Ionicons name={icon as any} size={20} color={tokens.color.primary} />
          </View>
          <View style={contactStyles.cardTitleContainer}>
            <Text style={contactStyles.cardTitle} numberOfLines={1}>{name}</Text>
            <View style={contactStyles.metaRow}>
              <Text style={contactStyles.metaText}>{formatTypeName(contact.contact_type.name)}</Text>
              <Text style={contactStyles.metaDivider}>•</Text>
              <Text style={contactStyles.metaText}>Added by {addedByLabel}</Text>
              <Text style={contactStyles.metaDivider}>•</Text>
              <View style={[contactStyles.sourceBadge, { backgroundColor: addColorOpacity(
                contact.source === 'property' ? tokens.color.success :
                contact.source === 'portfolio' ? tokens.color.primary : tokens.color.secondary, 0.1
              )}]}>
                <Text style={[contactStyles.sourceText, { color:
                  contact.source === 'property' ? tokens.color.success :
                  contact.source === 'portfolio' ? tokens.color.primary : tokens.color.secondary
                }]}>{sourceLabel}</Text>
              </View>
            </View>
          </View>
        </View>

        {/* Contact Info */}
        <View style={contactStyles.contactInfo}>
          {contact.email && (
            <Pressable
              onPress={() => handleEmail(contact.email!)}
              onLongPress={() => handleCopy(contact.email!, 'Email')}
              style={contactStyles.contactRow}
            >
              <Ionicons name="mail-outline" size={16} color={tokens.color.primary} />
              <Text style={contactStyles.contactText} numberOfLines={1}>{contact.email}</Text>
            </Pressable>
          )}
          {contact.phone && (
            <Pressable
              onPress={() => handleCall(contact.phone!)}
              onLongPress={() => handleCopy(contact.phone!, 'Phone')}
              style={contactStyles.contactRow}
            >
              <Ionicons name="call-outline" size={16} color={tokens.color.success} />
              <Text style={contactStyles.contactText}>{contact.phone}</Text>
            </Pressable>
          )}
          {contact.mobile && contact.mobile !== contact.phone && (
            <Pressable
              onPress={() => handleCall(contact.mobile!)}
              onLongPress={() => handleCopy(contact.mobile!, 'Mobile')}
              style={contactStyles.contactRow}
            >
              <Ionicons name="phone-portrait-outline" size={16} color={tokens.color.success} />
              <Text style={contactStyles.contactText}>{contact.mobile}</Text>
            </Pressable>
          )}
        </View>

        {/* Quick Actions */}
        <View style={contactStyles.quickActions}>
          {contact.phone && (
            <Pressable onPress={() => handleCall(contact.phone!)} style={contactStyles.actionButton}>
              <Ionicons name="call" size={18} color="#fff" />
            </Pressable>
          )}
          {contact.email && (
            <Pressable onPress={() => handleEmail(contact.email!)} style={[contactStyles.actionButton, { backgroundColor: tokens.color.primary }]}>
              <Ionicons name="mail" size={18} color="#fff" />
            </Pressable>
          )}
        </View>
      </Card>
    );
  };

  if (loading) {
    return (
      <View style={contactStyles.loadingContainer}>
        <ActivityIndicator size="large" color={tokens.color.primary} />
        <Text style={contactStyles.loadingText}>Loading contacts...</Text>
      </View>
    );
  }

  return (
    <View style={contactStyles.container}>
      {/* Header */}
      <View style={contactStyles.header}>
        <View>
          <Text style={contactStyles.headerTitle}>Property Contacts</Text>
          <Text style={contactStyles.headerSubtitle}>
            {contactsData?.total || 0} contacts from you and your partners
          </Text>
        </View>
        <Button
          title="Add Contact"
          onPress={() => setFormModalVisible(true)}
          variant="primary"
          size="sm"
          leftIcon="add-circle-outline"
        />
      </View>

      {/* Partners Info */}
      {contactsData && contactsData.partners.length > 1 && (
        <View style={contactStyles.partnersInfo}>
          <Ionicons name="people-outline" size={16} color={tokens.color.primary} />
          <Text style={contactStyles.partnersText}>
            Showing contacts from {contactsData.partners.length} partners
          </Text>
        </View>
      )}

      {/* Search and Filters */}
      <View style={contactStyles.filtersRow}>
        <Input
          placeholder="Search contacts..."
          value={searchQuery}
          onChangeText={setSearchQuery}
          style={contactStyles.searchInput}
        />
      </View>

      {/* Type Chips */}
      {availableTypes.length > 0 && (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={contactStyles.typeChipsScroll}
          contentContainerStyle={contactStyles.typeChipsContent}
        >
          <Pressable
            onPress={() => setSelectedType(null)}
            style={[contactStyles.typeChip, !selectedType && contactStyles.typeChipActive]}
          >
            <Text style={[contactStyles.typeChipText, !selectedType && contactStyles.typeChipTextActive]}>All</Text>
          </Pressable>
          {availableTypes.map(type => (
            <Pressable
              key={type}
              onPress={() => setSelectedType(selectedType === type ? null : type)}
              style={[contactStyles.typeChip, selectedType === type && contactStyles.typeChipActive]}
            >
              <Text style={[contactStyles.typeChipText, selectedType === type && contactStyles.typeChipTextActive]}>
                {formatTypeName(type)}
              </Text>
            </Pressable>
          ))}
        </ScrollView>
      )}

      {/* Contact List */}
      {filteredContacts.length === 0 ? (
        <View style={contactStyles.emptyState}>
          <Ionicons name="people-outline" size={48} color={addColorOpacity(tokens.color.onSurface, 0.3)} />
          <Text style={contactStyles.emptyTitle}>No contacts found</Text>
          <Text style={contactStyles.emptyText}>
            {searchQuery || selectedType
              ? 'Try adjusting your search or filters'
              : 'Add contacts to share them with your partners'}
          </Text>
        </View>
      ) : (
        <View style={contactStyles.contactGrid}>
          {filteredContacts.map(renderContactCard)}
        </View>
      )}

      {/* Add Contact Modal */}
      <ContactFormModal
        visible={formModalVisible}
        onClose={() => setFormModalVisible(false)}
        onSubmit={handleAddContact}
        contact={null}
        contactTypes={contactTypes}
        loading={saving}
      />
    </View>
  );
}

function makeContactStyles(
  tokens: ReturnType<typeof useStyles>['tokens'],
  utils: ReturnType<typeof useStyles>['utils'],
  isMobile: boolean,
  isSmallMobile: boolean,
  isDesktop: boolean
) {
  return StyleSheet.create({
    container: {
      flex: 1,
    },
    loadingContainer: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      padding: utils.spacing.xl,
    },
    loadingText: {
      marginTop: utils.spacing.md,
      fontSize: utils.typography.sizes.base,
      color: addColorOpacity(tokens.color.onSurface, 0.6),
      fontFamily: utils.typography.body.fontFamily,
    },
    header: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: utils.spacing.lg,
      flexWrap: 'wrap',
      gap: utils.spacing.md,
    },
    headerTitle: {
      fontSize: utils.typography.sizes.xl,
      fontWeight: utils.typography.weights.bold,
      color: tokens.color.onBackground,
      fontFamily: utils.typography.heading.fontFamily,
    },
    headerSubtitle: {
      fontSize: utils.typography.sizes.sm,
      color: addColorOpacity(tokens.color.onSurface, 0.6),
      fontFamily: utils.typography.body.fontFamily,
      marginTop: utils.spacing.xs,
    },
    partnersInfo: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: utils.spacing.xs,
      padding: utils.spacing.sm,
      backgroundColor: addColorOpacity(tokens.color.primary, 0.05),
      borderRadius: utils.radius.md,
      marginBottom: utils.spacing.md,
    },
    partnersText: {
      fontSize: utils.typography.sizes.sm,
      color: tokens.color.primary,
      fontFamily: utils.typography.body.fontFamily,
    },
    filtersRow: {
      flexDirection: 'row',
      gap: utils.spacing.md,
      marginBottom: utils.spacing.md,
    },
    searchInput: {
      flex: 1,
    },
    typeChipsScroll: {
      marginBottom: utils.spacing.lg,
    },
    typeChipsContent: {
      paddingRight: utils.spacing.lg,
    },
    typeChip: {
      paddingHorizontal: utils.spacing.md,
      paddingVertical: utils.spacing.sm,
      borderRadius: utils.radius.full,
      borderWidth: 1,
      borderColor: tokens.color.border,
      backgroundColor: tokens.color.surface,
      marginRight: utils.spacing.sm,
    },
    typeChipActive: {
      backgroundColor: tokens.color.primary,
      borderColor: tokens.color.primary,
    },
    typeChipText: {
      fontSize: utils.typography.sizes.sm,
      fontWeight: utils.typography.weights.medium,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.body.fontFamily,
    },
    typeChipTextActive: {
      color: tokens.color.onPrimary,
    },
    contactGrid: {
      ...Platform.select({
        web: {
          display: 'grid',
          gridTemplateColumns: isDesktop ? 'repeat(2, 1fr)' : '1fr',
          gap: utils.spacing.md,
        } as any,
        default: {
          gap: utils.spacing.md,
        },
      }),
    },
    contactCard: {
      padding: utils.spacing.md,
      marginBottom: Platform.OS === 'web' ? 0 : utils.spacing.md,
    },
    cardHeader: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: utils.spacing.sm,
      marginBottom: utils.spacing.md,
    },
    iconContainer: {
      width: 40,
      height: 40,
      borderRadius: utils.radius.md,
      backgroundColor: addColorOpacity(tokens.color.primary, 0.1),
      alignItems: 'center',
      justifyContent: 'center',
    },
    cardTitleContainer: {
      flex: 1,
      minWidth: 0,
    },
    cardTitle: {
      fontSize: utils.typography.sizes.lg,
      fontWeight: utils.typography.weights.semibold,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.heading.fontFamily,
    },
    metaRow: {
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'wrap',
      marginTop: utils.spacing.xs,
      gap: utils.spacing.xs,
    },
    metaText: {
      fontSize: utils.typography.sizes.xs,
      color: addColorOpacity(tokens.color.onSurface, 0.6),
      fontFamily: utils.typography.body.fontFamily,
    },
    metaDivider: {
      fontSize: utils.typography.sizes.xs,
      color: addColorOpacity(tokens.color.onSurface, 0.3),
    },
    sourceBadge: {
      paddingHorizontal: utils.spacing.xs,
      paddingVertical: 2,
      borderRadius: utils.radius.sm,
    },
    sourceText: {
      fontSize: 10,
      fontWeight: utils.typography.weights.semibold,
      fontFamily: utils.typography.body.fontFamily,
      textTransform: 'uppercase',
    },
    contactInfo: {
      gap: utils.spacing.xs,
      marginBottom: utils.spacing.md,
    },
    contactRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: utils.spacing.sm,
      paddingVertical: utils.spacing.xs,
      ...Platform.select({
        web: { cursor: 'pointer' } as any,
      }),
    },
    contactText: {
      fontSize: utils.typography.sizes.sm,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.body.fontFamily,
      flex: 1,
    },
    quickActions: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
      gap: utils.spacing.sm,
    },
    actionButton: {
      width: 36,
      height: 36,
      borderRadius: utils.radius.full,
      backgroundColor: tokens.color.success,
      alignItems: 'center',
      justifyContent: 'center',
      ...Platform.select({
        web: { cursor: 'pointer' } as any,
      }),
    },
    emptyState: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      padding: utils.spacing.xl,
      minHeight: 200,
    },
    emptyTitle: {
      fontSize: utils.typography.sizes.lg,
      fontWeight: utils.typography.weights.semibold,
      color: tokens.color.onSurface,
      marginTop: utils.spacing.md,
      fontFamily: utils.typography.heading.fontFamily,
    },
    emptyText: {
      fontSize: utils.typography.sizes.sm,
      color: addColorOpacity(tokens.color.onSurface, 0.6),
      textAlign: 'center',
      marginTop: utils.spacing.xs,
      fontFamily: utils.typography.body.fontFamily,
    },
  });
}

